import axios from "axios";
import { FormikErrors, useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Label } from "../../components/Label";
import { Loader } from "../../components/Loader";

interface RestaurantName {
  language: string;
  name: string;
}
interface Props {
  names: RestaurantName[];
  logo: File | null;
  token: string;
  branchs_token: string;
  system_type: string;
  name_api: string;
  categoryId: string;
  warrantyPeriod: number;
  recoveryPeriod: number;
  replacementPeriod: number;
}

export const EditRestaurants: React.FC = () => {
  const [preview, setPreview] = useState<string | null>(null);
  const { id } = useParams();

  const { mutate, isError, isLoading, data } = useMutation(
    async (data: any) => {
      return await axios.patch(`/restaurant/${id}`, data);
    }
  );
  const { data: restaurant, isLoading: restaurantLoading } = useQuery(
    "Get Restaurant",
    async () => {
      const response = await axios.get(`/restaurant/${id}`);
      return response.data.restaurant;
    }
  );

  const { data: categories, isLoading: isCategoriesLoading } = useQuery(
    "Get Categories",
    async () => {
      const response = await axios.get("/categories");
      return response.data.categories;
    }
  );

  const navigate = useNavigate();
  const formik = useFormik<Props>({
    initialValues: {
      names: [
        { language: "ar", name: "" },
        { language: "en", name: "" },
        { language: "ur", name: "" },
      ],
      logo: null,
      token: "",
      branchs_token: "",
      system_type: "",
      name_api: "",
      categoryId: "",
      warrantyPeriod: 0,
      recoveryPeriod: 0,
      replacementPeriod: 0,
    },
    validationSchema: Yup.object({
      names: Yup.array()
        .of(
          Yup.object({
            language: Yup.string().required(),
            name: Yup.string(),
          })
        )
        .min(1),
      token: Yup.string().required("Required"),
      branchs_token: Yup.string().required("Required"),
      system_type: Yup.string().required("Required"),
      categoryId: Yup.number().nullable(),
      warrantyPeriod: Yup.number().required("Required"),
      recoveryPeriod: Yup.number().required("Required"),
      replacementPeriod: Yup.number().required("Required"),
    }),
    onSubmit: async (values) => {
      const formData = new FormData();

      const names = values.names.filter((n) => n.name.trim() !== "");
      formData.append("names", JSON.stringify(names));

      formData.append("token", values.token);
      formData.append("branchs_token", values.branchs_token);
      formData.append("system_type", values.system_type);
      formData.append("name_api", values.name_api);
      formData.append("categoryId", values.categoryId.toString());
      if (values.logo) {
        formData.append("restaurantLogo", values.logo);
      }
      formData.append("warrantyPeriod", String(values.warrantyPeriod));
      formData.append("recoveryPeriod", String(values.recoveryPeriod));
      formData.append("replacementPeriod", String(values.replacementPeriod));

      try {
        await mutate(formData);
      } catch (error) {
        console.log(error);
      }
    },
  });

  useEffect(() => {
    if (restaurant) {
      if (Array.isArray(restaurant.translations)) {
        const updatedNames = formik.values.names.map((n) => {
          const match = restaurant?.translations?.find(
            (c: any) => c.language === n.language
          );
          return { ...n, name: match ? match.name : "" };
        });
        formik.setFieldValue("names", updatedNames);
      }
      formik.setFieldValue("logo", restaurant?.logo);
      formik.setFieldValue("token", restaurant?.token);
      formik.setFieldValue("branchs_token", restaurant?.branchs_token);
      formik.setFieldValue("system_type", restaurant?.system_type);
      formik.setFieldValue("categoryId", restaurant?.category?.id ?? "");
      formik.setFieldValue("warrantyPeriod", restaurant?.warrantyPeriod);
      formik.setFieldValue("recoveryPeriod", restaurant?.recoveryPeriod);
      formik.setFieldValue("replacementPeriod", restaurant?.replacementPeriod);
      setPreview(
        process.env.REACT_APP_UPLOAD_BASE_URL + restaurant?.logo || null
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant]);

  useEffect(() => {
    if (data && !isError) {
      navigate(`/dashboard/restaurants`);
    }
  }, [data, isError, navigate]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    formik.setFieldValue("logo", file);
    if (file) {
      setPreview(URL.createObjectURL(file));
    }
  };

  if (restaurantLoading) {
    return (
      <div className="flex justify-center items-center pt-32">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex">
      <form
        className="flex w-8/12 flex-col gap-4 "
        onSubmit={formik.handleSubmit}
      >
        <>
          <h3 className="text-3xl font-medium py-5">Edit Restaurant</h3>
          {formik.values.names.map((item, index) => (
            <div className="w-full" key={index}>
              <Label htmlFor={`names.${index}.name`}>
                Restaurant Name ({item.language.toUpperCase()})
              </Label>
              <Input
                type="text"
                id={`names.${index}.name`}
                className={`w-full ${
                  item.language === "ur" ? " h-full font-urdu" : ""
                }`}
                {...formik.getFieldProps(`names.${index}.name`)}
                error={
                  formik.touched.names?.[index]?.name
                    ? (
                        formik.errors.names?.[index] as FormikErrors<{
                          language: string;
                          name: string;
                        }>
                      )?.name || ""
                    : ""
                }
              />
            </div>
          ))}

          <div className="w-full">
            <Label htmlFor="logo">Logo</Label>
            <input
              type="file"
              id="logo"
              accept="image/*"
              onChange={handleLogoChange}
              className="w-full border px-3 py-2 rounded-md"
            />
            {preview && (
              <div className="mt-2">
                <img
                  src={preview}
                  alt="Logo Preview"
                  className="h-20 object-contain border"
                />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1 text-sm">
            <Label htmlFor="token">Token</Label>
            <textarea
              className={`px-5 py-3 rounded-md focus:outline-none h-20 bg-gray-50 border-gray-300 border-[1px] text-gray-900 ${
                formik.touched.token && formik.errors.token
                  ? " border border-red-500 bg-red-50 text-red-900 placeholder-red-700"
                  : ""
              }`}
              id="token"
              {...formik.getFieldProps("token")}
            ></textarea>
            {formik.touched.token && (
              <span className="px-1 text-xs  text-red-500">
                {formik.touched.token ? formik.errors.token : ""}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1 text-sm">
            <Label htmlFor="branchs_token">Branchs Token</Label>
            <textarea
              placeholder="Branchs Token"
              className={`px-5 py-3 rounded-md focus:outline-none h-20 bg-gray-50 border-gray-300 border-[1px] text-gray-900 ${
                formik.touched.branchs_token && formik.errors.branchs_token
                  ? " border border-red-500 bg-red-50 text-red-900 placeholder-red-700"
                  : ""
              }`}
              id="branchs_token"
              {...formik.getFieldProps("branchs_token")}
            ></textarea>
            {formik.touched.branchs_token && (
              <span className="px-1 text-xs  text-red-500">
                {formik.touched.branchs_token
                  ? formik.errors.branchs_token
                  : ""}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1 text-sm">
            <Label htmlFor="system_type">System Type</Label>
            <select
              className={`border-gray-300 border-[1px] px-5 py-3 rounded-md focus:outline-none bg-gray-50 text-gray-900 ${
                formik.touched.system_type
                  ? formik.errors.system_type
                    ? "border border-red-500 bg-red-50 text-red-900 placeholder-red-700"
                    : ""
                  : ""
              }`}
              id="type"
              {...formik.getFieldProps("system_type")}
            >
              <option disabled value="">
                Choose System Type
              </option>
              <option value="Foodics">Foodics</option>
              <option value="Specter">Specter</option>
            </select>
            {
              <span className="px-1 text-xs  text-red-500">
                {formik.touched.system_type ? formik.errors.system_type : ""}
              </span>
            }
          </div>

          <div className="flex flex-col gap-1 text-sm">
            <Label htmlFor="categoryId">Category</Label>
            {!isCategoriesLoading && !restaurantLoading && (
              <>
                <select
                  className={`border-gray-300 border-[1px] px-5 py-3 rounded-md focus:outline-none bg-gray-50 text-gray-900 ${
                    formik.touched.categoryId
                      ? formik.errors.categoryId
                        ? "border border-red-500 bg-red-50 text-red-900 placeholder-red-700"
                        : ""
                      : ""
                  }`}
                  id="categoryId"
                  {...formik.getFieldProps("categoryId")}
                >
                  <option value="">Choose Category</option>
                  {categories?.map((category: any) => (
                    <option
                      key={category.id}
                      value={category.id}
                      className="text-black"
                    >
                      {category?.name ||
                        category?.names?.find((n: any) => n.language === "en")
                          ?.name ||
                        "-"}
                    </option>
                  ))}
                </select>
                {
                  <span className="px-1 text-xs  text-red-500">
                    {formik.touched.categoryId ? formik.errors.categoryId : ""}
                  </span>
                }
              </>
            )}
          </div>

          <div className="w-full">
            <Label htmlFor="warrantyPeriod">Warranty Period</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                id="warrantyPeriod"
                className="w-full"
                {...formik.getFieldProps("warrantyPeriod")}
                error={
                  formik.touched.warrantyPeriod
                    ? formik.errors.warrantyPeriod
                    : ""
                }
              />
              <p className="font-semibold">Day</p>
            </div>
          </div>

          <div className="w-full">
            <Label htmlFor="recoveryPeriod">Recovery Period</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                id="recoveryPeriod"
                className="w-full"
                {...formik.getFieldProps("recoveryPeriod")}
                error={
                  formik.touched.recoveryPeriod
                    ? formik.errors.recoveryPeriod
                    : ""
                }
              />{" "}
              <p className="font-semibold">Day</p>
            </div>
          </div>

          <div className="w-full">
            <Label htmlFor="replacementPeriod">Replacement Period</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                id="replacementPeriod"
                className="w-full"
                {...formik.getFieldProps("replacementPeriod")}
                error={
                  formik.touched.replacementPeriod
                    ? formik.errors.replacementPeriod
                    : ""
                }
              />{" "}
              <p className="font-semibold">Day</p>
            </div>
          </div>

          <div className="flex gap-3 mb-10">
            <Button green type="submit" loading={isLoading}>
              Save
            </Button>
            <Button
              type="button"
              lightRed
              onClick={() => (window.location.href = "/dashboard/restaurants")}
            >
              Cancel
            </Button>
          </div>
        </>
      </form>
    </div>
  );
};
